import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  TablePagination,
  Chip,
  Stack,
  Container,
  Avatar,
  InputAdornment,
  IconButton,
  Backdrop,
  CircularProgress,
  Tooltip,
  useTheme,
  useMediaQuery,
  Grid,
  Button,
} from "@mui/material";
import {
  CalendarMonth,
  Search,
  Timer,
  Logout,
  Login,
  Visibility,
  FmdGood,
} from "@mui/icons-material";
import configServ from "../services/config";
import { useSelector } from "react-redux";
import AttendanceDetailDialog from "./AttendanceDetailDialog";
import pdfImage from "../Components/Assets/images/pdf.jfif";
import excelImage from "../Components/Assets/images/excel.png";
import dayjs from "dayjs";
import CardHeadMenu from "../Components/Card/CardHeadMenu/CardHeadMenu";
import CustomDateRangePicker from "../Components/DatePicker/CustomDateRangePicker";
import DownloadAttendancePdf from "../Components/Download/DownloadAttendancePdf";
import DownloadAttendanceExcel from "../Components/Download/DownloadAttendanceExcel";
import LiveMap from "../Map/LiveMap";
import { Dialog, DialogContent } from "@mui/material";

const Attendance = () => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [searchCustomer, setSearchCustomer] = useState("");

  const [dateRange, setDateRange] = useState([
    dayjs(), // start_date = today
    dayjs(), // end_date = today
  ]);

  const [openMapDialog, setOpenMapDialog] = useState(false);
  const [mapInfo, setMapInfo] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user_id, company_id, is_super, companyName } = useSelector(
    (state) => state.GlobalVariables,
  );

  const fetchAttendance = async () => {
    setLoading(true);
    try {
      const response = await configServ.getAttendanceForAdmin({
        company_id: company_id,
        page: page + 1,
        start_date: dateRange[0]
          ? dayjs(dateRange[0]).format("YYYY-MM-DD")
          : "",
        end_date: dateRange[1] ? dayjs(dateRange[1]).format("YYYY-MM-DD") : "",
        per_page: rowsPerPage,
        search: searchCustomer,
      });

      if (response.status) {
        setAttendanceData(response.data.data);
        setTotalRows(response.data.total);
      }
    } catch (error) {
      console.error("Error:", error);
      setAttendanceData([]);
      setTotalRows(0);
    } finally {
      // Thoda delay taaki smooth transition dikhe
      setTimeout(() => setLoading(false), 500);
    }
  };

  useEffect(() => {
    fetchAttendance();
  }, [page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
    fetchAttendance();
  }, [dateRange, searchCustomer]);

  // --- Components ---

  const [selectedDetail, setSelectedDetail] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [employeeName, setEmployeeName] = useState("");

  const handleOpenDetail = async (attendance) => {
    setLoading(true);
    setEmployeeName(attendance.employee_name);
    try {
      const response = await configServ.getAttendancePunchDetails({
        attendance_id: attendance.id,
        company_id: company_id,
      });
      if (response.status) {
        setSelectedDetail(response.data);
        setOpenDialog(true);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // const handleOpenMap = (attendance) => {
  //   const { latitude, longitude, site_name } = attendance;

  //   if (latitude && longitude) {
  //     const googleMapUrl = `https://www.google.com/maps?q=${latitude},${longitude}(${site_name || "Site"})`;
  //     window.open(googleMapUrl, "_blank");
  //   } else {
  //     alert("Location not available");
  //   }
  // };

  const handleOpenMap = (row) => {
  if (row.latitude && row.longitude) {
    setMapInfo({
      lat: parseFloat(row.latitude),
      lng: parseFloat(row.longitude),
      employee_name: row.employee_name,
      site_name: row.site_name,
    });
    setOpenMapDialog(true);
  }
};

  // pdf  and excel download handlers can be implemented here
  const [companyLogo, setCompanyLogo] = useState("");
  const [openBackdrop, setOpenBackdrop] = useState(false);

  const assignCompanyLogo = async () => {
    try {
      const result = await configServ.company_logo({ company_id: company_id });
      if (result && result.image) {
        // Assuming the result.image is already a base64 string
        setCompanyLogo(`data:image/png;base64,${result.image}`);
      } else {
        setCompanyLogo(null); // Set as null if no logo
      }
    } catch (e) {
      console.log(e);
      setCompanyLogo(null); // Handle error by clearing logo
    }
  };

  useEffect(() => {
    assignCompanyLogo();
  }, []);

  const handleDownloadPdf = () => {
    DownloadAttendancePdf(
      attendanceData,
      companyName,
      companyLogo,
      setOpenBackdrop,
    );
  };

  const handleDownloadExcel = () => {
    DownloadAttendanceExcel(attendanceData, companyName, setOpenBackdrop);
  };

  return (
    <Container sx={{ mt: 4, mb: 4, width: "100%" }}>
     <Dialog
  open={openMapDialog}
  onClose={() => setOpenMapDialog(false)}
  maxWidth="md"
  fullWidth
>
  <DialogContent>

    {/* 🔥 TOP HEADER */}
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 2,
        bgcolor: "#f5f9ff",
        border: "1px solid #e3f2fd",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1,
      }}
    >
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
          {mapInfo?.employee_name}
        </Typography>

        <Typography variant="body2" color="text.secondary">
          📍 {mapInfo?.site_name || "Main Site"}
        </Typography>
      </Box>

      {/* 🔥 DETAIL VIEW BUTTON */}
      <Button
        variant="contained"
        size="small"
        onClick={() => {
          const { lat, lng, site_name } = mapInfo;
          const googleMapUrl = `https://www.google.com/maps?q=${lat},${lng}(${site_name || "Site"})`;
          window.open(googleMapUrl, "_blank");
        }}
      >
        Detail View
      </Button>
    </Box>

    {/* 🗺 MAP */}
    {mapInfo && (
      <LiveMap
        lastLocation={{ lat: mapInfo.lat, lng: mapInfo.lng }}
        liveTracking={false}
      />
    )}

  </DialogContent>
</Dialog>
      <AttendanceDetailDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        detailData={selectedDetail}
        employeeName={employeeName}
        companyName={companyName}
        companyLogo={companyLogo}
        setOpenBackdrop={setOpenBackdrop}
      />
      {/* Professional Loader */}
      {/* <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop> */}

      <CardHeadMenu
        prefix={"Na"}
        suffix={"Na"}
        title={"ATTENDANCE RECORDS"}
        //handlePopup={handlePopup}
        //isEdit={isEdit}
        //create={isCreate}
        showCreate={false}
      />

      {/* Top Header Card */}
      <Card
        sx={{
          p: 1,
          mb: 3,
          borderRadius: "12px",
          borderLeft: "6px solid #1976d2",
          width: "100%",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          {/* Search Field */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Search Customer"
              size="small"
              value={searchCustomer}
              onChange={(e) => {
                setPage(0);
                setSearchCustomer(e.target.value);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Date Range */}
          <Grid item xs={12} sm={6} md={4}>
            <CustomDateRangePicker
              value={dateRange}
              onChange={setDateRange}
              fullWidth
            />
          </Grid>

          {/* Buttons */}
          <Grid
            item
            xs={12}
            md={5}
            sx={{
              display: "flex",
              justifyContent: { xs: "flex-start", md: "flex-end" },
              gap: 1,
            }}
          >
            <Tooltip title="Download PDF">
              <IconButton onClick={handleDownloadPdf}>
                <img
                  src={pdfImage}
                  alt="pdf"
                  style={{ width: 28, height: 28 }}
                />
              </IconButton>
            </Tooltip>

            <Tooltip title="Download Excel">
              <IconButton onClick={handleDownloadExcel}>
                <img
                  src={excelImage}
                  alt="excel"
                  style={{ width: 28, height: 28 }}
                />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Card>

      {/* Desktop Table View */}
      {!isMobile ? (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "12px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    bgcolor: "#1976d2",
                    color: "white",
                    fontWeight: "bold",
                  }}
                >
                  S. No
                </TableCell>
                <TableCell
                  sx={{
                    bgcolor: "#1976d2",
                    color: "white",
                    fontWeight: "bold",
                  }}
                >
                  Employee
                </TableCell>
                <TableCell
                  sx={{
                    bgcolor: "#1976d2",
                    color: "white",
                    fontWeight: "bold",
                  }}
                >
                  Date
                </TableCell>
                <TableCell
                  sx={{
                    bgcolor: "#1976d2",
                    color: "white",
                    fontWeight: "bold",
                  }}
                >
                  Punch In
                </TableCell>
                <TableCell
                  sx={{
                    bgcolor: "#1976d2",
                    color: "white",
                    fontWeight: "bold",
                  }}
                >
                  Last Punch Out
                </TableCell>
                <TableCell
                  sx={{
                    bgcolor: "#1976d2",
                    color: "white",
                    fontWeight: "bold",
                  }}
                >
                  Last Punch Site
                </TableCell>
                <TableCell
                  sx={{
                    bgcolor: "#1976d2",
                    color: "white",
                    fontWeight: "bold",
                  }}
                >
                  Duration
                </TableCell>
                <TableCell
                  sx={{
                    bgcolor: "#1976d2",
                    color: "white",
                    fontWeight: "bold",
                  }}
                  align="center"
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {attendanceData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography sx={{ py: 3, fontWeight: 500 }}>
                      No Data Found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                attendanceData.map((row, index) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>

                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          sx={{
                            bgcolor: "#e3f2fd",
                            color: "#1976d2",
                            fontWeight: "bold",
                          }}
                        >
                          {row.employee_name?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600 }}
                          >
                            {row.employee_name}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>

                    <TableCell sx={{ fontWeight: 500 }}>
                      {row.punch_date}
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={row.in_time?.split(" ")[1] || "--"}
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={row.out_time?.split(" ")[1] || "Pending"}
                        color={row.out_time ? "info" : "error"}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>

                    <TableCell sx={{ fontWeight: 500 }}>
                      {row.site_name || "Main Site"}
                    </TableCell>

                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Timer sx={{ fontSize: 16, color: "#1976d2" }} />
                        <Typography variant="body2">
                          {row.working_hours || "00:00"}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">

                        {/* View (Eye) */}
                        <IconButton onClick={() => handleOpenDetail(row)}>
                          <Visibility />
                        </IconButton>

                        {/* Map */}
                        <IconButton
                          color="success"
                          onClick={() => handleOpenMap(row)}
                          disabled={!row.latitude || !row.longitude} // optional safety
                        >
                          <FmdGood />
                        </IconButton>

                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        /* Mobile Card View */

        <Stack spacing={2}>
          {attendanceData.length === 0 ? (
            <Typography align="center" sx={{ mt: 4, fontWeight: 500 }}>
              No Data Found
            </Typography>
          ) : (
            attendanceData.map((row) => (
              <Card
                key={row.id}
                sx={{ p: 1, borderRadius: 3, border: "1px solid #e0e0e0" }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <Stack direction="row" spacing={2}>
                    <Avatar sx={{ bgcolor: "#1976d2" }}>
                      {row.employee_name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold" }}
                      >
                        {row.employee_name}
                      </Typography>
                      <Typography variant="caption" display="block">
                        {row.punch_date}
                      </Typography>
                      <Typography variant="caption" display="block">
                        <strong>Last Site: - </strong>
                        {row.site_name}
                      </Typography>
                    </Box>
                  </Stack>
                  <IconButton onClick={() => handleOpenDetail(row)}>
                    {" "}
                    <Visibility />{" "}
                  </IconButton>
                </Stack>
                <Box
                  sx={{
                    mt: 2,
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1,
                  }}
                >
                  <Box sx={{ p: 1, bgcolor: "#f1f8e9", borderRadius: 1 }}>
                    <Typography variant="caption" color="success.main">
                      IN TIME
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {row.in_time?.split(" ")[1]}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1, bgcolor: "#fff3e0", borderRadius: 1 }}>
                    <Typography variant="caption" color="warning.main">
                      OUT TIME
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {row.out_time?.split(" ")[1] || "---"}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            ))
          )}
        </Stack>
      )}

      <TablePagination
        rowsPerPageOptions={[20, 50, 100]}
        component="div"
        count={totalRows}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, p) => setPage(p)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        sx={{ mt: 2, bgcolor: "#f5f9ff", borderRadius: "0 0 12px 12px" }}
      />
    </Container>
  );
};

export default Attendance;
